/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useBookingActive, useSiteSettings } from '@/contexts/SiteSettingsContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Send, RotateCcw, Stethoscope, CreditCard, LogIn, CalendarCheck, CalendarOff, Phone } from 'lucide-react';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const TypingIndicator = () => (
  <div className="flex justify-start" data-testid="chat-typing-indicator">
    <div className="bg-white rounded-2xl rounded-tl-sm shadow-sm px-4 py-3 inline-flex items-center gap-1">
      {[0, 150, 300].map(delay => (
        <span
          key={delay}
          className="w-2 h-2 rounded-full bg-[#238A92]/60 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  </div>
);

export default function Prenota() {
  const { user, loading: authLoading } = useAuth();
  const bookingActive = useBookingActive();
  const siteSettings = useSiteSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const doctorId = searchParams.get('doctor');
  const serviceId = searchParams.get('service');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const initRef = useRef(false);

  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
  }, []);

  // Ogni navigazione verso la pagina di prenotazione azzera la chat e avvia un nuovo processo
  useEffect(() => {
    if (!user) return;
    if (initRef.current === location.key) return; // evita doppia init (StrictMode) sulla stessa navigazione
    initRef.current = location.key;
    const init = async () => {
      try {
        setLoadingHistory(false);
        setSending(true);
        setMessages([]);
        await axios.post(`${API}/booking-chat/reset`, {}, {});
        const s = await axios.post(`${API}/booking-chat/start`, { doctor_id: doctorId || null, service_id: serviceId || null }, {});
        setMessages([s.data.message]);
      } catch (err) {
        logger.error('Chat init error:', err);
        setMessages([{
          id: `err-${Date.now()}`, role: 'assistant',
          text: 'Benvenuto! Al momento ho un problema tecnico ad avviare la conversazione: riprovi tra qualche istante oppure telefoni allo studio.',
          created_at: new Date().toISOString()
        }]);
      } finally {
        setSending(false);
      }
    };
    init();
  }, [user, doctorId, serviceId, location.key]);

  useEffect(() => { scrollToBottom(messages.length <= 2 ? false : true); }, [messages, sending, scrollToBottom]);

  // Riporta il focus sul campo chat appena l'input torna abilitato (dopo ogni risposta)
  useEffect(() => {
    if (!sending && !loadingHistory && user) {
      inputRef.current?.focus();
    }
  }, [sending, loadingHistory, user]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const userMsg = { id: `local-${Date.now()}`, role: 'user', text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/booking-chat`, { message: text, doctor_id: doctorId || null, service_id: serviceId || null }, {});
      setMessages(prev => [...prev, res.data.message]);
    } catch (err) {
      logger.error('Chat error:', err);
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`, role: 'assistant',
        text: 'Si è verificato un problema tecnico. Riprovi tra qualche istante, oppure telefoni allo studio.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/booking-chat/reset`, {}, {});
      setMessages([]);
      setSending(true);
      const s = await axios.post(`${API}/booking-chat/start`, { doctor_id: doctorId || null, service_id: serviceId || null }, {});
      setMessages([s.data.message]);
      toast.success('Conversazione azzerata');
    } catch { toast.error('Errore'); } finally { setSending(false); }
  };

  const handlePayNow = async (appointmentId) => {
    setPayingId(appointmentId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        appointment_id: appointmentId,
        origin_url: window.location.origin
      }, {});
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'avvio del pagamento');
      setPayingId(null);
    }
  };

  // ── Prenotazioni online sospese (flag "Prenotazioni attive" in Impostazioni) ──
  if (!bookingActive) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full text-center shadow-[var(--shadow-md)]" data-testid="booking-disabled-card">
          <CardContent className="py-10">
            <div className="w-16 h-16 bg-[hsl(var(--secondary))] rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarOff size={30} className="text-[#238A92]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Prenotazioni online sospese</h2>
            <p className="text-muted-foreground mb-6">
              Al momento non è possibile prenotare online. Per fissare un appuntamento contatta direttamente lo studio.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {(siteSettings.telefono1 || siteSettings.telefono) && (
                <a href={`tel:${String(siteSettings.telefono1 || siteSettings.telefono).replace(/\s+/g, '')}`}>
                  <Button className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="booking-disabled-call-btn"><Phone size={16} className="mr-1" /> {siteSettings.telefono1 || siteSettings.telefono}</Button>
                </a>
              )}
              <Link to="/servizi"><Button variant="outline">Torna ai servizi</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Login richiesto ──
  if (!authLoading && !user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full text-center shadow-[var(--shadow-md)]" data-testid="chat-login-required">
          <CardContent className="py-10">
            <div className="w-16 h-16 bg-[#238A92] rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarCheck size={30} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Richiesta Appuntamento</h2>
            <p className="text-muted-foreground mb-6">
              Per prenotare tramite la chat con il nostro assistente è necessario accedere al proprio account.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate('/login')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="chat-login-btn">
                <LogIn size={16} className="mr-1" /> Accedi
              </Button>
              <Link to="/registrati"><Button variant="outline">Registrati</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] border flex flex-col" style={{ height: 'calc(100vh - 180px)', minHeight: '480px' }} data-testid="booking-chat">
        {/* Header stile WhatsApp */}
        <div className="bg-[#0F5E66] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
            <Stethoscope size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold leading-tight" style={{fontFamily:'Space Grotesk'}}>Studio Medico Boscovich</p>
            <p className="text-xs text-white/80 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Assistente prenotazioni — online
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/15 hover:text-white h-8 px-2" title="Azzera conversazione" data-testid="chat-reset-btn">
                <RotateCcw size={15} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Azzerare la conversazione?</AlertDialogTitle>
                <AlertDialogDescription>La cronologia della chat verrà cancellata. Gli appuntamenti già prenotati NON verranno toccati.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset} className="bg-[#238A92]">Azzera</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Area messaggi */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-2.5 bg-[#EDF3F2]" data-testid="chat-messages-area">
          {loadingHistory ? (
            <div className="flex justify-center py-10">
              <TypingIndicator />
            </div>
          ) : (
            <>
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.role}`}>
                  <div className={`max-w-[85%] sm:max-w-[75%] px-3.5 py-2 shadow-sm text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-[#CFEAE6] text-[#123B36] rounded-2xl rounded-tr-sm'
                      : 'bg-white text-[#1F2A37] rounded-2xl rounded-tl-sm'
                  }`}>
                    {m.text}
                    {m.payment?.appointment_id && (
                      <div className="mt-2.5">
                        <Button
                          size="sm"
                          onClick={() => handlePayNow(m.payment.appointment_id)}
                          disabled={payingId === m.payment.appointment_id}
                          className="bg-[#1E7A58] hover:bg-[#155C42] text-white w-full"
                          data-testid="chat-pay-now-btn"
                        >
                          <CreditCard size={14} className="mr-1.5" />
                          {payingId === m.payment.appointment_id ? 'Avvio pagamento...' : `Paga ora ${Number(m.payment.amount).toFixed(2)} EUR`}
                        </Button>
                      </div>
                    )}
                    <p className={`text-[10px] mt-1 text-right ${m.role === 'user' ? 'text-[#123B36]/50' : 'text-muted-foreground'}`}>
                      {fmtTime(m.created_at)}
                    </p>
                  </div>
                </div>
              ))}
              {sending && <TypingIndicator />}
            </>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Barra input */}
        <div className="bg-white border-t px-3 py-3 flex items-center gap-2 flex-shrink-0">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Scriva qui il suo messaggio... (es. Vorrei prenotare una visita)"
            disabled={sending || loadingHistory}
            maxLength={1000}
            className="flex-1 h-11 rounded-full border border-input bg-[hsl(var(--secondary))] px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92] disabled:opacity-60"
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || sending || loadingHistory}
            className="h-11 w-11 rounded-full bg-[#238A92] hover:bg-[#0F5E66] text-white p-0 flex-shrink-0"
            title="Invia"
            data-testid="chat-send-btn"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-3">
        L'assistente non fornisce consigli medici. Per urgenze contatti direttamente lo studio. I suoi appuntamenti restano visibili in{' '}
        <Link to="/i-miei-appuntamenti" className="text-[#238A92] hover:underline">I Miei Appuntamenti</Link>.
      </p>
    </div>
  );
}
